






































































































import { Component, Vue } from "vue-property-decorator";
import moment from "moment";
import PortalInput from "@/components/common/PortalInput.vue";
import PortalSelect from "@/components/common/PortalSelect.vue";
import SingleFileHolder from "@/components/util/SingleFileHolder.vue";
import TaxObligationDTO from "@/dto/tax/TaxObligationDTO";
import TaxObligationPayload from "@/dto/tax/TaxObligationPayload";
import TaxObligationService from "@/services/TaxObligationService";
import Application from "@/state/Application";
import { processError } from "@/utils/ComponentUtils";
import { YEARS, Period } from "@/constants/DateTimeConstants";
import { TaxReportForm } from "@/constants/TaxReportForm";
import SelectOption from "@/components/common/SelectOption";

@Component({
  components: {
    PortalInput,
    PortalSelect,
    SingleFileHolder
  }
})
export default class EditTaxObligationModal extends Vue {
  private taxObligationId: number | null = null;
  private form: TaxObligationPayload | null = null;

  beforeOpen(event: any) {
    const obligation = event.params.taxObligation as TaxObligationDTO;
    this.taxObligationId = obligation.id;
    
    // Initialize payload from existing DTO
    this.form = {
      year: obligation.year,
      period: obligation.period,
      periodStart: obligation.periodStart,
      periodEnd: obligation.periodEnd,
      reportFilingDeadline: obligation.reportFilingDeadline,
      paidOn: obligation.paidOn,
      status: obligation.status,
      obligation: obligation.obligation,
      paymentStatus: obligation.paymentStatus,
      risk: obligation.risk,
      nextAction: obligation.nextAction,
      assigneeId: obligation.assigneeId,
      taxReportForm: obligation.taxReportForm,
      state: obligation.state,
      customerId: obligation.customerId,
      customerType: obligation.customerType,
      submissionId: obligation.submissionId,
      filingDate: obligation.filingDate,
      submittedForm: obligation.submittedForm,
      documents: (obligation.documents || []).map(d => ({
          type: d.type,
          comment: d.comment,
          fileId: d.file.id
      })),
      liabilities: obligation.liabilities || []
    } as any;

    if (this.$validator) {
      this.$validator.reset();
    }
  }

  closeModal() {
    this.$modal.hide("edit-tax-obligation-modal");
  }

  get isValid(): boolean {
    if (this.$validator && this.$validator.errors.items.length > 0) return false;
    return !!(this.form && this.form.year && this.form.period && this.form.periodStart && this.form.periodEnd && this.form.reportFilingDeadline);
  }

  get yearOptions() {
    return YEARS.map(year => SelectOption.builder().title(`${year}`).value(year).build());
  }

  get periodOptions() {
    return Object.values(Period).map(period => SelectOption.builder().title(this.$t('period.enum.' + period) as string).value(period).build());
  }

  public updatePeriodDates(): void {
    if (!this.form || !this.form.year || !this.form.period) {
      return;
    }

    const start = moment().year(this.form.year);
    const end = moment().year(this.form.year);

    switch (this.form.period) {
      case Period.JANUARY: start.month(0).startOf('month'); end.month(0).endOf('month'); break;
      case Period.FEBRUARY: start.month(1).startOf('month'); end.month(1).endOf('month'); break;
      case Period.MARCH: start.month(2).startOf('month'); end.month(2).endOf('month'); break;
      case Period.APRIL: start.month(3).startOf('month'); end.month(3).endOf('month'); break;
      case Period.MAY: start.month(4).startOf('month'); end.month(4).endOf('month'); break;
      case Period.JUNE: start.month(5).startOf('month'); end.month(5).endOf('month'); break;
      case Period.JULY: start.month(6).startOf('month'); end.month(6).endOf('month'); break;
      case Period.AUGUST: start.month(7).startOf('month'); end.month(7).endOf('month'); break;
      case Period.SEPTEMBER: start.month(8).startOf('month'); end.month(8).endOf('month'); break;
      case Period.OCTOBER: start.month(9).startOf('month'); end.month(9).endOf('month'); break;
      case Period.NOVEMBER: start.month(10).startOf('month'); end.month(10).endOf('month'); break;
      case Period.DECEMBER: start.month(11).startOf('month'); end.month(11).endOf('month'); break;

      case Period.QUARTER_1: start.quarter(1).startOf('quarter'); end.quarter(1).endOf('quarter'); break;
      case Period.QUARTER_2: start.quarter(2).startOf('quarter'); end.quarter(2).endOf('quarter'); break;
      case Period.QUARTER_3: start.quarter(3).startOf('quarter'); end.quarter(3).endOf('quarter'); break;
      case Period.QUARTER_4: start.quarter(4).startOf('quarter'); end.quarter(4).endOf('quarter'); break;

      case Period.WINTER:
        start.month(11).subtract(1, 'year').startOf('month');
        end.month(1).endOf('month');
        break;
      case Period.SPRING:
        start.month(2).startOf('month');
        end.month(4).endOf('month');
        break;
      case Period.SUMMER:
        start.month(5).startOf('month');
        end.month(7).endOf('month');
        break;
      case Period.FALL:
        start.month(8).startOf('month');
        end.month(10).endOf('month');
        break;
      case Period.FULL_YEAR:
        start.startOf('year');
        end.endOf('year');
        break;
    }

    this.form.periodStart = start.format('YYYY-MM-DD');
    this.form.periodEnd = end.format('YYYY-MM-DD');
    this.updateReportFilingDeadline();
  }

  public updateReportFilingDeadline(): void {
    if (!this.form || !this.form.taxReportForm || !this.form.periodEnd) {
      return;
    }

    const periodEnd = moment(this.form.periodEnd);
    let deadline: moment.Moment | null = null;

    switch (this.form.taxReportForm) {
      case TaxReportForm.FORM_941:
        deadline = periodEnd.add(1, 'month').endOf('month');
        break;
      case TaxReportForm.FORM_940:
        deadline = periodEnd.add(1, 'year').month(0).date(31);
        break;
      case TaxReportForm.FORM_1120:
        deadline = periodEnd.add(3, 'months').add(15, 'days');
        break;
      case TaxReportForm.FORM_1120S:
      case TaxReportForm.FORM_1065:
        deadline = periodEnd.add(2, 'months').add(15, 'days');
        break;
      case TaxReportForm.SALES_TAX_REPORT:
        deadline = periodEnd.add(1, 'month').date(20);
        break;
      default:
        break;
    }

    if (deadline) {
      this.form.reportFilingDeadline = deadline.format('YYYY-MM-DD');
    }
  }

  async submit() {
    const valid = await this.$validator.validateAll();
    if (!valid || !this.isValid || !this.taxObligationId || !this.form) return;

    Application.startLoading();
    TaxObligationService.updateTaxObligation(this.taxObligationId, this.form).then(
      () => {
        Application.stopLoading();
        this.$emit("updated");
        this.closeModal();
      },
      err => {
        Application.stopLoading();
        processError(err, this);
      }
    );
  }
}
